import { useEffect, useState } from "react";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Star, ClipboardList, Settings, BarChart3, X, Copy, Send, FileText,
    Plus, ExternalLink, AlertTriangle, CheckCircle2, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatDateTime, formatTime } from "@/lib/format";

const TABS = [
    { key: "overview",  label: "Overview",   icon: BarChart3 },
    { key: "templates", label: "Templates",  icon: FileText },
    { key: "responses", label: "Responses",  icon: ClipboardList },
    { key: "settings",  label: "Settings",   icon: Settings },
];

const STATUS_BADGE = {
    sent:      "bg-muted text-muted-foreground",
    opened:    "bg-blue-50 text-blue-700",
    submitted: "bg-emerald-50 text-emerald-700",
};

export default function Surveys() {
    const [tab, setTab] = useState("overview");
    return (
        <>
            <Topbar title="Surveys & Reviews" subtitle="Post-stay feedback · NPS · Review requests" />
            <div className="flex-1 overflow-auto p-4 md:p-6">
                <div className="mb-4 flex gap-1 border-b border-border" data-testid="surveys-tabs">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                data-testid={`surveys-tab-${t.key}`}
                                className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[12px] font-medium transition ${
                                    tab === t.key
                                        ? "border-[#ff7b02] text-[#ff7b02]"
                                        : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                <Icon size={13} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>
                {tab === "overview"  && <OverviewPane />}
                {tab === "templates" && <TemplatesPane />}
                {tab === "responses" && <ResponsesPane />}
                {tab === "settings"  && <SettingsPane />}
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────
function OverviewPane() {
    const [data, setData] = useState(null);
    useEffect(() => {
        (async () => {
            try { setData((await api.get("/surveys/reports/summary")).data); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        })();
    }, []);
    if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    const kpis = [
        { label: "Sent",          value: data.sent,                 icon: Send },
        { label: "Responses",     value: data.responses,            icon: ClipboardList },
        { label: "Response rate", value: `${data.response_rate_pct}%`, icon: BarChart3 },
        { label: "Avg rating",    value: data.avg_rating || "—",    icon: Star },
        { label: "NPS",           value: data.nps != null ? data.nps : "—", icon: BarChart3 },
        { label: "Follow-ups",    value: data.open_followups,       icon: AlertTriangle },
    ];

    return (
        <div className="space-y-6" data-testid="surveys-overview">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                {kpis.map((c) => (
                    <div key={c.label} className="akm-card p-4">
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                            <c.icon size={14} className="text-muted-foreground" />
                        </div>
                        <div className="mt-1 text-xl font-semibold tabular-nums"
                             data-testid={`surveys-kpi-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            {c.value}
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="akm-card overflow-hidden">
                    <div className="border-b border-border bg-muted/30 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Ratings distribution
                    </div>
                    <div className="p-4 space-y-2">
                        {[5, 4, 3, 2, 1].map((rate) => {
                            const row = (data.by_rating || []).find((r) => r.rating === rate);
                            const count = row?.count || 0;
                            const max = Math.max(1, ...(data.by_rating || []).map((r) => r.count));
                            const pct = Math.round((count / max) * 100);
                            return (
                                <div key={rate} className="flex items-center gap-2 text-[12px]">
                                    <div className="w-12 flex items-center gap-0.5">
                                        {rate}<Star size={11} className="fill-amber-400 text-amber-400" />
                                    </div>
                                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                        <div className="h-full bg-[#ff7b02]" style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="w-8 text-right tabular-nums text-muted-foreground">{count}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="akm-card overflow-hidden">
                    <div className="border-b border-border bg-muted/30 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        NPS breakdown
                    </div>
                    <div className="p-4 space-y-3">
                        <div className="flex items-center gap-2 text-[13px]">
                            <span className="w-24 text-muted-foreground">Promoters</span>
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                <div className="h-full bg-emerald-500" style={{ width: `${pct(data.promoters, data.promoters + data.passives + data.detractors)}%` }} />
                            </div>
                            <span className="w-10 text-right tabular-nums">{data.promoters}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[13px]">
                            <span className="w-24 text-muted-foreground">Passives</span>
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                <div className="h-full bg-amber-400" style={{ width: `${pct(data.passives, data.promoters + data.passives + data.detractors)}%` }} />
                            </div>
                            <span className="w-10 text-right tabular-nums">{data.passives}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[13px]">
                            <span className="w-24 text-muted-foreground">Detractors</span>
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                <div className="h-full bg-red-500" style={{ width: `${pct(data.detractors, data.promoters + data.passives + data.detractors)}%` }} />
                            </div>
                            <span className="w-10 text-right tabular-nums">{data.detractors}</span>
                        </div>
                    </div>
                </div>

                <div className="akm-card overflow-hidden md:col-span-2">
                    <div className="border-b border-border bg-muted/30 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Top complaint categories
                    </div>
                    <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                        {(data.by_complaint || []).map((c) => (
                            <div key={c.category} className="rounded-md border border-border p-2 text-[12.5px]">
                                <div className="capitalize font-medium">{c.category}</div>
                                <div className="text-[18px] font-semibold tabular-nums">{c.count}</div>
                            </div>
                        ))}
                        {(data.by_complaint || []).length === 0 && (
                            <div className="col-span-full text-[12px] text-muted-foreground">No complaints reported in this period.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function pct(n, total) { return total ? Math.round((n / total) * 100) : 0; }

// ─────────────────────────────────────────────────────────────
// Templates
// ─────────────────────────────────────────────────────────────
function TemplatesPane() {
    const [rows, setRows] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: "", subject: "", intro: "", google_review_url: "" });

    const load = async () => {
        try { setRows((await api.get("/survey-templates")).data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.name) return toast.error("Name required");
        try {
            await api.post("/survey-templates", { ...form, default_for: rows.length === 0 ? "post_stay" : "" });
            toast.success("Template created");
            setShowForm(false);
            setForm({ name: "", subject: "", intro: "", google_review_url: "" });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete template?")) return;
        await api.delete(`/survey-templates/${id}`);
        await load();
    };

    return (
        <div className="space-y-3" data-testid="surveys-templates">
            <div className="flex items-center justify-between">
                <div className="text-[12px] text-muted-foreground">
                    Build the questions guests see when they complete your post-stay survey.
                </div>
                <button onClick={() => setShowForm((s) => !s)} className="akm-btn-primary text-[12px]"
                        data-testid="surveys-template-new">
                    <Plus size={12} /> New template
                </button>
            </div>

            {showForm && (
                <div className="akm-card p-4 space-y-2" data-testid="surveys-template-form">
                    <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="st-name" />
                    <Input label="Email subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="st-subject" />
                    <Input label="Intro" value={form.intro} onChange={(v) => setForm({ ...form, intro: v })} testid="st-intro" />
                    <Input label="Google review URL (shown after high rating)"
                           value={form.google_review_url}
                           onChange={(v) => setForm({ ...form, google_review_url: v })} testid="st-google" />
                    <div className="text-[11px] text-muted-foreground">
                        Default questions (rating × 5 + NPS + free text + complaint category) will be added automatically. Customize later.
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowForm(false)} className="akm-btn-ghost text-[12px]">Cancel</button>
                        <button onClick={create} className="akm-btn-primary text-[12px]" data-testid="surveys-template-save">Create</button>
                    </div>
                </div>
            )}

            <div className="akm-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Subject</th>
                            <th className="px-3 py-2 text-left">Default for</th>
                            <th className="px-3 py-2 text-right">Questions</th>
                            <th className="px-3 py-2 text-center">Active</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                                <td className="px-3 py-2 font-medium">{r.name}</td>
                                <td className="px-3 py-2 text-muted-foreground">{r.subject}</td>
                                <td className="px-3 py-2 text-muted-foreground">{r.default_for || "—"}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{(r.questions || []).length}</td>
                                <td className="px-3 py-2 text-center">
                                    {r.active ? <CheckCircle2 size={14} className="inline text-emerald-600" /> : <X size={14} className="inline text-muted-foreground" />}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => del(r.id)} className="akm-btn-ghost text-red-700 text-[11px]"
                                            data-testid={`st-del-${r.id}`}>Delete</button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan="6" className="py-10 text-center text-muted-foreground">
                                No templates yet. A default template will be created automatically on first send.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Responses
// ─────────────────────────────────────────────────────────────
function ResponsesPane() {
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("submitted");
    const [rating, setRating] = useState("");
    const [active, setActive] = useState(null);

    const load = async () => {
        try {
            const p = new URLSearchParams();
            if (status) p.set("status", status);
            if (rating) p.set("rating", rating);
            setRows((await api.get(`/surveys?${p.toString()}`)).data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, rating]);

    return (
        <div className="space-y-3" data-testid="surveys-responses">
            <div className="flex flex-wrap items-center gap-2">
                {["all", "sent", "submitted"].map((s) => (
                    <button key={s} onClick={() => setStatus(s)}
                            data-testid={`sr-status-${s}`}
                            className={`rounded-md border px-3 py-1.5 text-[12px] font-medium ${
                                status === s ? "border-[#ff7b02] bg-[#ff7b02]/10 text-[#ff7b02]" :
                                "border-border text-muted-foreground hover:bg-muted/40"
                            }`}>
                        {s === "all" ? "All" : s === "sent" ? "Pending" : "Submitted"}
                    </button>
                ))}
                <select value={rating} onChange={(e) => setRating(e.target.value)} className="akm-input text-[12px]"
                        data-testid="sr-rating-filter">
                    <option value="">All ratings</option>
                    <option value="ge4">≥ 4★</option>
                    <option value="lt3">&lt; 3★ (poor)</option>
                    <option value="5">Exactly 5★</option>
                </select>
            </div>

            <div className="akm-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Guest</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-right">Rating</th>
                            <th className="px-3 py-2 text-right">NPS</th>
                            <th className="px-3 py-2 text-left">Complaint</th>
                            <th className="px-3 py-2 text-left">Sent</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                                <td className="px-3 py-2 font-medium">{r.guest_name || "—"}</td>
                                <td className="px-3 py-2">
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[r.status]}`}>{r.status}</span>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {r.overall_rating != null ? (
                                        <span className="inline-flex items-center gap-0.5 tabular-nums font-semibold">
                                            {r.overall_rating}<Star size={11} className="fill-amber-400 text-amber-400" />
                                        </span>
                                    ) : "—"}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{r.nps_score != null ? r.nps_score : "—"}</td>
                                <td className="px-3 py-2">
                                    {r.complaint_category
                                        ? <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] text-red-700 capitalize">{r.complaint_category}</span>
                                        : "—"}
                                </td>
                                <td className="px-3 py-2 text-[11px] text-muted-foreground">
                                    {r.sent_at ? formatDate(r.sent_at) : "—"}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => setActive(r.id)} className="akm-btn-ghost text-[11px]"
                                            data-testid={`sr-open-${r.id}`}>Open</button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan="7" className="py-10 text-center text-muted-foreground">No responses</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {active && <ResponseDrawer id={active} onClose={() => setActive(null)} onChanged={load} />}
        </div>
    );
}

function ResponseDrawer({ id, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [note, setNote] = useState("");
    const [followStatus, setFollowStatus] = useState("in_progress");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try { setData((await api.get(`/surveys/${id}`)).data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const saveFollow = async () => {
        setBusy(true);
        try {
            await api.post(`/surveys/${id}/follow-up`, { note, status: followStatus });
            toast.success("Follow-up saved");
            await load(); await onChanged();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!data) {
        return (
            <div className="fixed inset-0 z-50 flex" data-testid="surveys-drawer">
                <div className="flex-1 bg-foreground/30" onClick={onClose} />
                <div className="flex h-full w-full max-w-[680px] flex-col bg-background p-6 text-sm text-muted-foreground">Loading…</div>
            </div>
        );
    }
    const { survey, template, reservation, followup } = data;
    const qmap = {};
    (template?.questions || []).forEach((q) => qmap[q.id] = q);

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="surveys-drawer">
            <div className="flex-1 bg-foreground/30" onClick={onClose} />
            <div className="flex h-full w-full max-w-[680px] flex-col overflow-y-auto border-l border-border bg-background shadow-2xl">
                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                    <div>
                        <div className="text-[13px] font-semibold tracking-tight">Survey response</div>
                        <div className="text-[11px] text-muted-foreground">{template?.name}</div>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-base font-semibold">{survey.guest_name || "—"}</div>
                            <div className="text-[12px] text-muted-foreground">
                                Stay: {reservation?.check_in} → {reservation?.check_out}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                                Sent {survey.sent_at ? formatDateTime(survey.sent_at) : "—"} ·
                                {survey.submitted_at ? ` Submitted ${formatDateTime(survey.submitted_at)}` : " Awaiting reply"}
                            </div>
                        </div>
                        {survey.overall_rating != null && (
                            <div className="text-right">
                                <div className="text-2xl font-semibold tabular-nums text-[#ff7b02]">
                                    {survey.overall_rating}<span className="text-[11px] text-muted-foreground"> / 5</span>
                                </div>
                                {survey.nps_score != null && <div className="text-[11px] text-muted-foreground">NPS {survey.nps_score}</div>}
                            </div>
                        )}
                    </div>

                    {survey.status === "submitted" && survey.answers && (
                        <div className="rounded-md border border-border overflow-hidden">
                            <div className="border-b border-border bg-muted/30 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Answers</div>
                            <div className="p-3 space-y-2">
                                {Object.entries(survey.answers || {}).map(([qid, val]) => {
                                    const q = qmap[qid];
                                    if (!q) return null;
                                    return (
                                        <div key={qid} className="grid grid-cols-3 gap-2 text-[12.5px]">
                                            <div className="col-span-2 text-muted-foreground">{q.label}</div>
                                            <div className="text-right font-medium">
                                                {q.type === "rating" ? (
                                                    <span className="inline-flex items-center gap-0.5">
                                                        {val}<Star size={11} className="fill-amber-400 text-amber-400" />
                                                    </span>
                                                ) : q.type === "nps" ? (
                                                    <span className="tabular-nums">{val} / 10</span>
                                                ) : (
                                                    <span>{String(val || "—")}</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {survey.comment && (
                        <div className="rounded-md border border-border bg-muted/20 p-3 text-[12.5px]">
                            <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Guest comment</div>
                            <MessageSquare size={12} className="inline mr-1 text-muted-foreground" />
                            {survey.comment}
                        </div>
                    )}

                    {survey.complaint_category && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-800">
                            <AlertTriangle size={12} className="inline mr-1" />
                            Complaint category: <span className="font-semibold capitalize">{survey.complaint_category}</span>
                        </div>
                    )}

                    <div className="rounded-md border border-border p-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Staff follow-up</div>
                        {followup && (
                            <div className="mb-2 text-[12px] text-muted-foreground">
                                Status: <span className="font-semibold capitalize">{followup.status}</span>
                                {followup.note && <> · "{followup.note}"</>}
                            </div>
                        )}
                        <select value={followStatus} onChange={(e) => setFollowStatus(e.target.value)}
                                className="akm-input w-full text-[13px] mb-2" data-testid="sr-follow-status">
                            <option value="open">Open</option>
                            <option value="in_progress">In progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                        </select>
                        <textarea value={note} onChange={(e) => setNote(e.target.value)}
                                  placeholder="Internal follow-up note"
                                  className="akm-input w-full text-[13px]" rows={2}
                                  data-testid="sr-follow-note" />
                        <div className="mt-2 flex justify-end">
                            <button onClick={saveFollow} disabled={busy} className="akm-btn-primary text-[12px]"
                                    data-testid="sr-follow-save">
                                Save follow-up
                            </button>
                        </div>
                    </div>

                    {template?.google_review_url && (
                        <a href={template.google_review_url} target="_blank" rel="noreferrer"
                           className="akm-btn-ghost text-[12px]">
                            <ExternalLink size={12} /> Google review link
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────────
function SettingsPane() {
    // Settings here = create-or-update default template's google_review_url
    // + send test survey UI
    const [reservationId, setReservationId] = useState("");
    const [recentLinks, setRecentLinks] = useState([]);

    const send = async () => {
        if (!reservationId) return toast.error("Reservation ID required");
        try {
            const { data } = await api.post("/surveys/send", { reservation_id: reservationId, channel: "email" });
            const link = `${window.location.origin}/s/${data.token}`;
            navigator.clipboard.writeText(link).catch(() => {});
            setRecentLinks((s) => [{ id: data.id, link, ts: formatTime() }, ...s].slice(0, 5));
            toast.success("Survey sent (link copied)");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-4" data-testid="surveys-settings">
            <div className="akm-card p-4 space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Send a survey manually</div>
                <div className="text-[12px] text-muted-foreground">
                    Surveys are sent automatically when reservations are marked as <strong>checked-out</strong>.
                    You can also send one manually for any reservation.
                </div>
                <div className="flex gap-2">
                    <input value={reservationId} onChange={(e) => setReservationId(e.target.value)}
                           placeholder="Reservation ID (UUID)"
                           className="akm-input flex-1 text-[13px]" data-testid="ss-reservation-id" />
                    <button onClick={send} className="akm-btn-primary text-[12px]" data-testid="ss-send">
                        <Send size={12} /> Send
                    </button>
                </div>
                {recentLinks.length > 0 && (
                    <div className="space-y-1">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Recent links</div>
                        {recentLinks.map((l) => (
                            <div key={l.id} className="flex items-center gap-2 text-[11px]">
                                <code className="flex-1 truncate font-mono text-[11px] text-[#ff7b02]">{l.link}</code>
                                <button onClick={() => { navigator.clipboard.writeText(l.link); toast.success("Copied"); }}
                                        className="akm-btn-ghost text-[10px]">
                                    <Copy size={10} />
                                </button>
                                <span className="text-muted-foreground">{l.ts}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="akm-card p-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">How it works</div>
                <ul className="list-disc list-inside text-[12.5px] text-muted-foreground space-y-1">
                    <li>When a guest is marked <strong>checked out</strong>, a post-stay survey is auto-sent (idempotent).</li>
                    <li>Guests open a public link <code>/s/&lt;token&gt;</code>, rate categories (1–5★), give NPS (0–10) and optional comment.</li>
                    <li>Low ratings (&lt;3) or complaint categories auto-create a staff follow-up task.</li>
                    <li>High ratings (≥4) show your Google review link in the thank-you screen.</li>
                    <li>Configure Google review URL per template under the Templates tab.</li>
                </ul>
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", testid }) {
    return (
        <label className="block">
            <div className="text-[11px] font-medium text-muted-foreground mb-1">{label}</div>
            <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                   className="akm-input w-full text-[13px]" data-testid={testid} />
        </label>
    );
}
